import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useAfterFirstPaint } from '@/lib/useAfterFirstPaint';

/**
 * The mini chat: one window, reachable from every page, that keeps the thread
 * you were reading while you go and look at something else.
 *
 * Same shape as the assistant's window (AiAssistantContext) on purpose —
 * a reply should not cost you your place, wherever you are in the app. See
 * docs/MESSAGING.md.
 */
export type ChatWindowState = 'closed' | 'open';

interface ChatWindowApi {
  state: ChatWindowState;
  /** Which conversation the window is showing; null shows the list. */
  conversationId: number | null;
  open: (conversationId?: number | null) => void;
  close: () => void;
  toggle: () => void;
  show: (conversationId: number | null) => void;
}

const STORAGE_PREFIX = 'mm:chatWindow:v1:';
const ChatWindowContext = createContext<ChatWindowApi | null>(null);

function storageKey(userId: number | null): string | null {
  return userId ? `${STORAGE_PREFIX}${userId}` : null;
}

export function ChatWindowProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useAuth();
  // Restored once the page has painted: with the stored user present from the
  // first render (AuthContext), restoring the chat window at once landed in the
  // tasks that draw the screen (a table scored ~3 points lower for it).
  const shellReady = useAfterFirstPaint();
  const userId = shellReady ? (currentUser?.id ?? null) : null;
  const [state, setState] = useState<ChatWindowState>('closed');
  const [conversationId, setConversationId] = useState<number | null>(null);

  // Restore per user; a different user in the same browser starts closed.
  useEffect(() => {
    const key = storageKey(userId);
    if (!key) {
      setState('closed');
      setConversationId(null);
      return;
    }
    try {
      const stored = JSON.parse(localStorage.getItem(key) ?? 'null') as {
        state?: ChatWindowState;
        conversationId?: number | null;
      } | null;
      setState(stored?.state === 'open' ? 'open' : 'closed');
      setConversationId(stored?.conversationId ?? null);
    } catch {
      setState('closed');
    }
  }, [userId]);

  useEffect(() => {
    const key = storageKey(userId);
    if (!key) return;
    try {
      localStorage.setItem(key, JSON.stringify({ state, conversationId }));
    } catch {
      // A browser with site data blocked still gets a working window.
    }
  }, [state, conversationId, userId]);

  const open = useCallback((id?: number | null) => {
    if (id !== undefined) setConversationId(id);
    setState('open');
  }, []);

  const close = useCallback(() => setState('closed'), []);
  const toggle = useCallback(() => setState((prev) => (prev === 'open' ? 'closed' : 'open')), []);
  const show = useCallback((id: number | null) => setConversationId(id), []);

  const value = useMemo(
    () => ({ state, conversationId, open, close, toggle, show }),
    [state, conversationId, open, close, toggle, show],
  );

  return <ChatWindowContext.Provider value={value}>{children}</ChatWindowContext.Provider>;
}

export function useChatWindow(): ChatWindowApi {
  const ctx = useContext(ChatWindowContext);
  if (!ctx) throw new Error('useChatWindow must be used inside <ChatWindowProvider>');
  return ctx;
}
